/**
 * overlayLogoOnImage
 *
 * Uses HTML Canvas to composite a logo onto a main image.
 * The logo is placed in the top-left corner with padding and an optional
 * semi-transparent backing for visibility.
 *
 * Returns a JPEG Blob of the result, or the original image Blob on error.
 */

export interface LogoOverlayOptions {
  /** Logo size as a fraction of the main image width (0-1). Default 0.15 */
  logoScale?: number;
  /** Padding in pixels from the corner. Default 24 */
  padding?: number;
  /** Corner where logo is placed. Default 'top-left' */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** JPEG quality (0-1). Default 0.92 */
  quality?: number;
  /** If true, add a semi-transparent white backdrop behind the logo. Default true */
  withBackdrop?: boolean;
}

/**
 * Load an image from a URL using fetch() to avoid CORS tainted canvas issues.
 * Fetches the image as a blob, then creates an object URL for the Image element.
 */
async function loadImage(url: string): Promise<HTMLImageElement> {
  try {
    // Use fetch to download the image as a blob (avoids CORS canvas taint)
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // Don't revoke immediately — canvas needs the reference
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`Failed to decode image: ${url}`));
      };
      img.src = objectUrl;
    });
  } catch (fetchError) {
    // Fallback: try direct loading with crossOrigin
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  }
}

/**
 * Overlay a logo onto a main image and return the result as a JPEG Blob.
 *
 * @param mainImageUrl  URL of the main/base image
 * @param logoUrl       URL of the logo to overlay
 * @param options       Optional configuration
 * @returns             JPEG Blob with the logo composited onto the image
 */
export async function overlayLogoOnImage(
  mainImageUrl: string,
  logoUrl: string,
  options: LogoOverlayOptions = {}
): Promise<Blob> {
  const {
    logoScale = 0.15,
    padding = 24,
    position = 'top-left',
    quality = 0.92,
    withBackdrop = true,
  } = options;

  // Load both images in parallel
  const [mainImg, logoImg] = await Promise.all([
    loadImage(mainImageUrl),
    loadImage(logoUrl),
  ]);

  // Create canvas matching the main image dimensions
  const canvas = document.createElement('canvas');
  canvas.width = mainImg.naturalWidth;
  canvas.height = mainImg.naturalHeight;
  const ctx = canvas.getContext('2d')!;

  // Draw main image
  ctx.drawImage(mainImg, 0, 0);

  // Calculate logo dimensions (maintain aspect ratio)
  const maxLogoWidth = canvas.width * logoScale;
  const logoAspect = logoImg.naturalWidth / logoImg.naturalHeight;
  let logoWidth = maxLogoWidth;
  let logoHeight = logoWidth / logoAspect;

  // If logo is very tall, constrain by height instead
  const maxLogoHeight = canvas.height * logoScale;
  if (logoHeight > maxLogoHeight) {
    logoHeight = maxLogoHeight;
    logoWidth = logoHeight * logoAspect;
  }

  // Calculate position
  let logoX = padding;
  let logoY = padding;

  switch (position) {
    case 'top-right':
      logoX = canvas.width - logoWidth - padding;
      logoY = padding;
      break;
    case 'bottom-left':
      logoX = padding;
      logoY = canvas.height - logoHeight - padding;
      break;
    case 'bottom-right':
      logoX = canvas.width - logoWidth - padding;
      logoY = canvas.height - logoHeight - padding;
      break;
    case 'top-left':
    default:
      logoX = padding;
      logoY = padding;
      break;
  }

  // Draw semi-transparent backdrop if enabled
  if (withBackdrop) {
    const backdropPadding = 8;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';

    // Rounded rectangle backdrop
    const bx = logoX - backdropPadding;
    const by = logoY - backdropPadding;
    const bw = logoWidth + backdropPadding * 2;
    const bh = logoHeight + backdropPadding * 2;
    const radius = 6;

    ctx.beginPath();
    ctx.moveTo(bx + radius, by);
    ctx.lineTo(bx + bw - radius, by);
    ctx.arcTo(bx + bw, by, bx + bw, by + radius, radius);
    ctx.lineTo(bx + bw, by + bh - radius);
    ctx.arcTo(bx + bw, by + bh, bx + bw - radius, by + bh, radius);
    ctx.lineTo(bx + radius, by + bh);
    ctx.arcTo(bx, by + bh, bx, by + bh - radius, radius);
    ctx.lineTo(bx, by + radius);
    ctx.arcTo(bx, by, bx + radius, by, radius);
    ctx.closePath();
    ctx.fill();
  }

  // Draw logo
  ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);

  // Convert to JPEG Blob
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to generate image with logo overlay'));
      },
      'image/jpeg',
      quality
    );
  });
}

/**
 * Safe wrapper: overlays logo onto image, uploading result to Supabase storage.
 * If anything fails, returns the original image URL unchanged.
 */
export async function overlayLogoAndUpload(
  mainImageUrl: string,
  logoUrl: string | null | undefined,
  uploadFn: (file: File) => Promise<{ publicUrl: string | null; error: any }>,
  options?: LogoOverlayOptions
): Promise<string> {
  // If no logo, return original
  if (!logoUrl || !mainImageUrl) {
    return mainImageUrl;
  }

  try {
    const blob = await overlayLogoOnImage(mainImageUrl, logoUrl, options);
    const file = new File([blob], `logo_overlay_${Date.now()}.jpg`, { type: 'image/jpeg' });
    const { publicUrl, error } = await uploadFn(file);

    if (error || !publicUrl) {
      console.error('[overlayLogo] Upload failed, using original image:', error);
      return mainImageUrl;
    }

    return publicUrl;
  } catch (error) {
    console.error('[overlayLogo] Overlay failed, using original image:', error);
    return mainImageUrl;
  }
}

/**
 * Composite an AI-generated photo with a branded template overlay.
 * 
 * The original n8n approach: AI photo fills the ENTIRE image as background,
 * and the branded template (logo, decorative elements) is overlaid ON TOP.
 * The template has transparent areas where the AI photo shows through.
 *
 * @param templateUrl  URL of the branded template (from task's image_url)
 * @param aiPhotoUrl   URL of the AI-generated photo (from webhook response)
 * @param quality      JPEG quality (0-1). Default 0.92
 * @returns            JPEG Blob with template overlaid on the AI photo
 */
export async function compositePhotoOnTemplate(
  templateUrl: string,
  aiPhotoUrl: string,
  quality: number = 0.92
): Promise<Blob> {
  const [templateImg, photoImg] = await Promise.all([
    loadImage(templateUrl),
    loadImage(aiPhotoUrl),
  ]);

  const canvas = document.createElement('canvas');
  canvas.width = templateImg.naturalWidth;
  canvas.height = templateImg.naturalHeight;
  const ctx = canvas.getContext('2d')!;

  const tplW = canvas.width;
  const tplH = canvas.height;
  const photoW = photoImg.naturalWidth;
  const photoH = photoImg.naturalHeight;

  // STEP 1: Draw AI photo as FULL background (cover mode — fills entire canvas)
  const photoAspect = photoW / photoH;
  const canvasAspect = tplW / tplH;

  let drawW: number, drawH: number, drawX: number, drawY: number;

  if (photoAspect > canvasAspect) {
    // Photo is wider — fit height, crop width
    drawH = tplH;
    drawW = tplH * photoAspect;
    drawX = (tplW - drawW) / 2;
    drawY = 0;
  } else {
    // Photo is taller — fit width, crop height
    drawW = tplW;
    drawH = tplW / photoAspect;
    drawX = 0;
    drawY = (tplH - drawH) / 2;
  }

  ctx.drawImage(photoImg, drawX, drawY, drawW, drawH);

  // STEP 2: Overlay template ON TOP (logo, decorative elements)
  // Template has transparent areas where AI photo shows through
  ctx.drawImage(templateImg, 0, 0, tplW, tplH);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to generate composited image'));
      },
      'image/jpeg',
      quality
    );
  });
}


/**
 * Composite AI photo onto branded template, upload result.
 * Falls back to original AI photo URL on failure.
 */
export async function compositeAndUpload(
  templateUrl: string | null | undefined,
  aiPhotoUrl: string,
  uploadFn: (file: File) => Promise<{ publicUrl: string | null; error: any }>
): Promise<string> {
  if (!templateUrl || !aiPhotoUrl || templateUrl === aiPhotoUrl) {
    return aiPhotoUrl;
  }

  try {
    console.log('[compositeAndUpload] Compositing AI photo onto template...');
    const blob = await compositePhotoOnTemplate(templateUrl, aiPhotoUrl);
    const file = new File([blob], `composite_${Date.now()}.jpg`, { type: 'image/jpeg' });
    const { publicUrl, error } = await uploadFn(file);

    if (error || !publicUrl) {
      console.error('[compositeAndUpload] Upload failed:', error);
      return aiPhotoUrl;
    }

    console.log('[compositeAndUpload] Composite uploaded:', publicUrl);
    return publicUrl;
  } catch (error) {
    console.error('[compositeAndUpload] Composite failed:', error);
    return aiPhotoUrl;
  }
}

